// Civil date/time helpers. "Local seconds" means seconds since
// 1970-01-01T00:00 computed from wall-clock fields as if they were UTC.

export interface Civil {
  year: number;
  month: number; // 1-12
  day: number; // 1-31
  hour: number;
  minute: number;
  second: number;
  weekday: number; // 0 = Sunday
}

export function toCivil(localSeconds: number): Civil {
  const d = new Date(localSeconds * 1000);
  return {
    year: d.getUTCFullYear(),
    month: d.getUTCMonth() + 1,
    day: d.getUTCDate(),
    hour: d.getUTCHours(),
    minute: d.getUTCMinutes(),
    second: d.getUTCSeconds(),
    weekday: d.getUTCDay(),
  };
}

export function fromCivil(year: number, month: number, day: number, hour = 0, minute = 0, second = 0): number {
  return Date.UTC(year, month - 1, day, hour, minute, second) / 1000;
}

export const pad2 = (n: number) => String(n).padStart(2, "0");

export function isoDate(c: Civil): string {
  return `${c.year}-${pad2(c.month)}-${pad2(c.day)}`;
}

export function parseIsoDate(s: string): { year: number; month: number; day: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const year = +m[1];
  const month = +m[2];
  const day = +m[3];
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year, month, day };
}

/** Parse "14:30", "2:30pm", "2 pm", "1430", "14:30:15". */
export function parseTime(s: string): { hour: number; minute: number; second: number } | null {
  const t = s.trim().toLowerCase();
  const m = /^(\d{1,2})(?::?(\d{2}))?(?::(\d{2}))?\s*(a\.?m\.?|p\.?m\.?)?$/.exec(t);
  if (!m) return null;
  let hour = +m[1];
  const minute = +(m[2] ?? 0);
  const second = +(m[3] ?? 0);
  const ampm = m[4]?.[0];
  if (ampm) {
    if (hour < 1 || hour > 12) return null;
    if (ampm === "a" && hour === 12) hour = 0;
    if (ampm === "p" && hour !== 12) hour += 12;
  }
  if (hour > 23 || minute > 59 || second > 59) return null;
  return { hour, minute, second };
}

export type TimeFormat = "24h" | "12h";

export function formatTime(c: Civil, fmt: TimeFormat, withSeconds = false): string {
  const secs = withSeconds ? `:${pad2(c.second)}` : "";
  if (fmt === "24h") return `${pad2(c.hour)}:${pad2(c.minute)}${secs}`;
  const h = c.hour % 12 || 12;
  return `${h}:${pad2(c.minute)}${secs} ${c.hour < 12 ? "am" : "pm"}`;
}

export function formatOffset(utoff: number): string {
  const sign = utoff < 0 ? "-" : "+";
  const a = Math.abs(utoff);
  const h = Math.floor(a / 3600);
  const m = Math.floor((a % 3600) / 60);
  const s = a % 60;
  return `${sign}${pad2(h)}:${pad2(m)}${s ? `:${pad2(s)}` : ""}`;
}

export const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
export const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
export const MONTHS_SHORT = MONTHS.map((m) => m.slice(0, 3));

export function formatDate(c: Civil): string {
  return `${WEEKDAYS[c.weekday]} ${c.day} ${MONTHS_SHORT[c.month - 1]} ${c.year}`;
}
